import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetailPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products/${productId}`);
        setProduct(response.data.product);
      } catch (error) {
        console.error('Error fetching product:', error);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const handleDownloadPDF = () => {
    window.open(`${API_URL}/api/products/${productId}/pdf`, '_blank');
  };

  if (loading) {
    return (
      <div className="bg-charcoal min-h-screen pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-bronze border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="bg-charcoal min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="heading-display text-stone text-4xl mb-4">Product Not Found</h1>
          <Link to="/products" className="btn btn-outline">
            <ArrowLeft size={16} className="mr-2" />
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const steelComparison = {
    'tensile_strength': '90,000 psi',
    'break_load_4': '~18,000 lbf',
    'weight_per_foot': '0.668 lb/ft',
    'modulus_of_elasticity': '29 Msi',
    'corrosion_resistance': 'None',
    'temperature_range': 'Degrades >400°C'
  };

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="product-detail-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-border-dark relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link 
            to="/products" 
            className="inline-flex items-center text-stone/60 hover:text-stone text-sm mb-8 transition-colors"
            data-testid="back-to-products"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Products
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-mono text-bronze text-xs px-2 py-1 border border-bronze">
                  {product.type}
                </span>
              </div>

              <h1 className="heading-display text-stone text-5xl md:text-6xl mb-4" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-bronze text-xl mb-6">{product.tagline}</p>
              <p className="text-stone/70 leading-relaxed mb-8 text-lg">
                {product.description}
              </p>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={handleDownloadPDF}
                  className="btn btn-primary"
                  data-testid="download-pdf-btn"
                >
                  Download Spec Sheet
                  <Download size={18} className="ml-2" />
                </button>
                <Link 
                  to="/contact" 
                  className="btn btn-outline"
                  data-testid="request-quote-btn"
                >
                  Request Quote
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square bg-gradient-to-br from-bronze/20 via-surface-dark to-charcoal border border-border-dark flex items-center justify-center">
                {product.type === 'Basalt Geogrid' || product.type === 'Basalt Mesh' ? (
                  <div className="grid grid-cols-6 gap-2 opacity-40">
                    {[...Array(36)].map((_, i) => (
                      <div key={i} className="w-8 h-8 border border-stone/40" />
                    ))}
                  </div>
                ) : product.type === 'Chopped Basalt Fiber' ? (
                  <div className="flex flex-wrap gap-1 opacity-50 p-4">
                    {[...Array(30)].map((_, i) => (
                      <div key={i} className="w-1 bg-bronze/60" style={{ height: `${12 + Math.random() * 16}px`, transform: `rotate(${Math.random() * 60 - 30}deg)` }} />
                    ))}
                  </div>
                ) : (
                  <div className={`w-48 h-3 ${product.type === 'BFRP' ? 'bg-bronze/60' : 'bg-stone/40'}`} />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Material Info */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-bronze text-mono text-sm tracking-widest mb-4">MATERIAL COMPOSITION</p>
              <h2 className="heading-section text-stone text-2xl mb-4">What It Is</h2>
              <p className="text-stone/70 leading-relaxed">{product.material}</p>
              
              {product.available_sizes && (
                <div className="mt-6">
                  <p className="text-stone/50 text-sm mb-2">Available Sizes:</p>
                  <div className="flex flex-wrap gap-2">
                    {product.available_sizes.map((size, index) => (
                      <span key={index} className="text-mono text-stone text-sm px-3 py-1 bg-surface-dark border border-border-dark">
                        {size}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {product.available_formats && (
                <div className="mt-6">
                  <p className="text-stone/50 text-sm mb-2">Available Formats:</p>
                  <ul className="space-y-1">
                    {product.available_formats.map((format, index) => (
                      <li key={index} className="text-stone/70 text-sm flex items-center gap-2">
                        <CheckCircle size={14} className="text-bronze" />
                        {format}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <p className="text-bronze text-mono text-sm tracking-widest mb-4">DESIGN STANDARD</p>
              <h2 className="heading-section text-stone text-2xl mb-4">Code Compliance</h2>
              <p className="text-stone/70 leading-relaxed mb-4">
                {product.design_standard}
              </p>
              {product.id === 'geobar-b' && (
                <p className="text-stone/50 text-sm">
                  Note: BFRP is deflection-governed, not strength-governed. Engineers should reference ACI 440.1R-15 for design guidance.
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 lg:py-24 section-light" data-testid="specs-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">PERFORMANCE DATA</p>
            <h2 className="heading-display text-charcoal text-4xl">
              TECHNICAL SPECIFICATIONS
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="overflow-x-auto"
          >
            <table className="spec-table" data-testid="product-specs-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>{product.name}</th>
                  <th>Grade 60 Steel</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(product.specs).map(([key, value]) => (
                  <tr key={key}>
                    <td>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                    <td className="text-bronze font-medium">{value}</td>
                    <td className={steelComparison[key] === 'None' ? 'text-stone/40' : ''}>
                      {steelComparison[key] || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">USE CASES</p>
            <h2 className="heading-display text-stone text-4xl">
              APPLICATIONS
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {product.applications.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="p-6 border border-border-dark bg-surface-dark"
              >
                <CheckCircle size={20} className="text-bronze mb-3" />
                <p className="text-stone text-sm">{app}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      {product.key_benefits && (
        <section className="py-16 lg:py-24 bg-surface-dark" data-testid="benefits-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <p className="text-bronze text-mono text-sm tracking-widest mb-4">WHY {product.name.toUpperCase()}</p>
              <h2 className="heading-display text-stone text-4xl">
                KEY BENEFITS
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {product.key_benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="p-6 border border-border-dark bg-charcoal"
                >
                  <CheckCircle size={24} className="text-bronze mb-4" />
                  <p className="text-stone text-sm leading-relaxed">{benefit}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Certifications */}
      {product.certifications && (
        <section className="py-12 border-t border-border-dark">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-wrap items-center justify-center gap-8">
              <span className="text-stone/40 text-sm">Certifications:</span>
              {product.certifications.map((cert, index) => (
                <span key={index} className="text-mono text-stone/60 text-xs tracking-wider">
                  {cert}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-surface-dark border-t border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="heading-section text-stone text-2xl mb-2">Ready to specify {product.name}?</h3>
              <p className="text-stone/60">Request a sample kit or project quote today.</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleDownloadPDF}
                className="btn btn-outline"
                data-testid="cta-download-pdf"
              >
                Download PDF
                <Download size={18} className="ml-2" />
              </button>
              <Link 
                to="/contact" 
                className="btn btn-primary"
                data-testid="cta-request-quote"
              >
                Request Quote
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
